import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useUpdateUser } from '@modules/common/hooks/useUpdateUser';
import { PushPin, PushPinOutlined } from '@mui/icons-material';
import {
  Box,
  IconButton,
  IconButtonProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Unpin } from 'common/Icons/Icons';
import { ReactNode, useEffect, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface PinDashboardIconProps {
  dashboardID: string;
  iconColor?: string;
  iconEdge?: IconButtonProps['edge'];
  onChange: (isPinned: boolean) => void;
}

const MAX_PINNED_DASHBOARDS_ALLOWED = 5;

export const PinDashboardIcon = ({
  dashboardID,
  iconColor,
  iconEdge,
  onChange,
}: PinDashboardIconProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const { pinnedDashboards = [] } = useGetUser();
  const { updateUser, loading: isUpdatingUser } = useUpdateUser();

  useEffect(() => {
    if (isUpdatingUser && isHovering) {
      setIsHovering(false);
    }
  }, [isUpdatingUser, isHovering]);

  const isPinnedDashboard = pinnedDashboards.some(
    (pinnedDashboard) => pinnedDashboard.id === dashboardID,
  );

  const canPinDashboard =
    pinnedDashboards.length < MAX_PINNED_DASHBOARDS_ALLOWED;

  const getTooltipTitle = (): NonNullable<ReactNode> => {
    if (isPinnedDashboard) {
      return 'Unpin Dashboard';
    }
    if (pinnedDashboards.length >= MAX_PINNED_DASHBOARDS_ALLOWED) {
      return (
        <>
          <Typography variant="inherit">
            {MAX_PINNED_DASHBOARDS_ALLOWED} Dashboards are already pinned.
          </Typography>
          <Typography variant="inherit">
            Unpin one of the Dashboards to pin this one.
          </Typography>
        </>
      );
    }
    return 'Pin Dashboard';
  };

  const pinDashboard = async () => {
    if (canPinDashboard) {
      await updateUser({
        addPinnedDashboardIDs: [dashboardID],
      });
      onChange(true);
    }
  };

  const unPinDashboard = async () => {
    await updateUser({ removePinnedDashboardIDs: [dashboardID] });
    onChange(false);
  };

  const getPinIcon = () => {
    if (isPinnedDashboard && isHovering) {
      return <Unpin width="16px" height="16px" fill={iconColor} />;
    }
    if (isPinnedDashboard || isHovering) {
      return <PushPin fontSize="inherit" sx={{ color: iconColor }} />;
    }
    return <PushPinOutlined fontSize="inherit" sx={{ color: iconColor }} />;
  };

  if (isUpdatingUser) {
    return <CircularLoader size={16} />;
  }

  return (
    <Tooltip title={getTooltipTitle()} placement="right">
      <Box>
        <IconButton
          size="small"
          edge={iconEdge}
          onClick={isPinnedDashboard ? unPinDashboard : pinDashboard}
          sx={{
            transform: 'rotate(45deg)',
            color: iconColor,
          }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          disabled={!isPinnedDashboard && !canPinDashboard}
          aria-label={
            isPinnedDashboard
              ? 'unpin-dashboard-button'
              : 'pin-dashboard-button'
          }
        >
          {getPinIcon()}
        </IconButton>
      </Box>
    </Tooltip>
  );
};
